import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw, Sparkles } from "lucide-react";

const BUBBLE_COUNT = 10;
const BUBBLE_COLORS = [
  "from-[#a69373]/60 to-[#a69373]/30",
  "from-[#5a8a8a]/60 to-[#5a8a8a]/30",
  "from-[#8a7a5a]/60 to-[#8a7a5a]/30",
  "from-[#6a8a6a]/60 to-[#6a8a6a]/30",
  "from-[#7a6a8a]/60 to-[#7a6a8a]/30",
];

const generateBubble = (id) => ({
  id,
  x: 10 + Math.random() * 80, // 10-90% from left
  size: 40 + Math.random() * 30, // 40-70px
  speed: 3 + Math.random() * 4, // 3-7 seconds to float up
  delay: Math.random() * 2, // 0-2 second delay
  color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)],
  popped: false,
  y: 110, // Start below viewport
});

export function BubblePopGame() {
  const [bubbles, setBubbles] = useState([]);
  const [poppedCount, setPoppedCount] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const containerRef = useRef(null);

  const initGame = useCallback(() => {
    const newBubbles = Array.from({ length: BUBBLE_COUNT }, (_, i) => generateBubble(i));
    setBubbles(newBubbles);
    setPoppedCount(0);
    setGameComplete(false);
    setGameStarted(true);
  }, []);

  const popBubble = useCallback((id) => {
    setBubbles(prev => prev.map(b => 
      b.id === id ? { ...b, popped: true } : b
    ));
    setPoppedCount(prev => {
      const newCount = prev + 1;
      if (newCount >= BUBBLE_COUNT) {
        setTimeout(() => setGameComplete(true), 500);
      }
      return newCount;
    });
  }, []);

  // Reset game
  const resetGame = useCallback(() => {
    setGameStarted(false);
    setGameComplete(false);
    setPoppedCount(0);
    setBubbles([]);
  }, []);

  if (!gameStarted) {
    return (
      <Card className="glass-card overflow-hidden" data-testid="bubble-pop-start">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-[#a69373]" />
          </div>
          <h3 className="text-lg font-heading text-white mb-2">Stress Release</h3>
          <p className="text-white/60 text-sm mb-4">
            Pop 10 bubbles to release tension and reset your mind.
          </p>
          <Button
            onClick={initGame}
            className="bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold"
            data-testid="start-bubble-game"
          >
            Start Popping
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (gameComplete) {
    return (
      <Card className="glass-card overflow-hidden" data-testid="bubble-pop-complete">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#a69373]/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-10 h-10 text-[#a69373]" />
          </div>
          <h3 className="text-xl font-heading text-[#a69373] mb-2">Well done.</h3>
          <p className="text-white/70 text-lg mb-6">Take a breath.</p>
          <Button
            onClick={resetGame}
            variant="outline"
            className="border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
            data-testid="reset-bubble-game"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Play Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card overflow-hidden" data-testid="bubble-pop-game">
      <CardContent className="p-0">
        {/* Progress indicator */}
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <span className="text-white/60 text-sm">Bubbles popped</span>
          <span className="text-[#a69373] font-semibold">{poppedCount} / {BUBBLE_COUNT}</span>
        </div>
        
        {/* Game area */}
        <div 
          ref={containerRef}
          className="relative h-64 overflow-hidden bg-gradient-to-b from-transparent to-[#2d4a44]/50"
          style={{ touchAction: 'none' }}
        >
          {bubbles.map((bubble) => (
            <div
              key={bubble.id}
              className={`absolute cursor-pointer transition-all ${
                bubble.popped 
                  ? 'scale-150 opacity-0' 
                  : 'hover:scale-110'
              }`}
              style={{
                left: `${bubble.x}%`,
                width: bubble.size,
                height: bubble.size,
                transform: `translateX(-50%)`,
                animation: bubble.popped 
                  ? 'none' 
                  : `float-up ${bubble.speed}s linear ${bubble.delay}s infinite`,
                pointerEvents: bubble.popped ? 'none' : 'auto',
              }}
              onClick={() => !bubble.popped && popBubble(bubble.id)}
              data-testid={`bubble-${bubble.id}`}
            >
              <div 
                className={`w-full h-full rounded-full bg-gradient-to-br ${bubble.color} 
                  border border-white/20 shadow-lg backdrop-blur-sm
                  flex items-center justify-center transition-transform duration-200`}
              >
                <div className="w-1/3 h-1/3 rounded-full bg-white/30 -translate-x-1/4 -translate-y-1/4" />
              </div>
            </div>
          ))}
        </div>

        {/* Hint */}
        <div className="px-4 py-2 text-center">
          <p className="text-white/40 text-xs">Tap or click the bubbles to pop them</p>
        </div>
      </CardContent>

      <style>{`
        @keyframes float-up {
          0% {
            bottom: -80px;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            bottom: 300px;
            opacity: 0;
          }
        }
      `}</style>
    </Card>
  );
}

export default BubblePopGame;
